import { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { SLICES } from '../../constants/slices';
import { useResponsiveConfig } from '../../hooks/responsiveConfigSwap';
import { BREAK_POINT } from '../../components/renewal/consts';

import { clearAdjustedSummary, setRenewalSummary } from '../../store/slice/renewalHubSlice';

import { CSPUrlsMap } from '../../routes/CSPUrlsMap';

import {
  useGetRenewalEligibilityQuery,
  useGetRenewalSummaryQuery,
  useCreateRenewalQuoteQuery,
  useSubmitRenewalMutation
} from '../../data/renewal';

import { PRODUCT_CODE } from '../../constants/renewalHub';
import { getGtmService } from '../../services/gtmService/GtmService';

export const useRenewal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { adjustedSummary } = useSelector((state) => state?.[SLICES.RENEWAL_HUB]);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const policyNumber = params.get('policy');

  useLayoutEffect(() => {
    // Avoid a brief sticky-summary flash on reload when adjusted values are restored from session storage.
    dispatch(clearAdjustedSummary());
  }, [dispatch, policyNumber]);

  const [submitRenewal, { isLoading: submitRenewalIsLoading, isError: submitRenewalIsError }] =
    useSubmitRenewalMutation();
  const [submitRenewalHasError, setSubmitRenewalHasError] = useState(false);
  const [unableToSubmit, setUnableToSubmit] = useState(false);

  const { isMobile } = useResponsiveConfig({
    configDefinition: { default: { isMobile: false }, [BREAK_POINT]: { isMobile: true } }
  });

  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;

  const {
    data: renewalEligibilityData,
    isLoading: renewalEligibilityIsLoading,
    isError: renewalEligibilityIsError
  } = useGetRenewalEligibilityQuery(auth0AccessToken, { skip: !auth0AccessToken, refetchOnMountOrArgChange: true });

  const renewal =
    !renewalEligibilityIsLoading &&
    !renewalEligibilityIsError &&
    renewalEligibilityData?.find((item) => item.PolicyNumber === policyNumber);

  const {
    data: renewalSummaryData,
    isLoading: renewalSummaryIsLoading,
    isError: renewalSummaryIsError
  } = useGetRenewalSummaryQuery(
    { auth0Token: auth0AccessToken, policyNumber },
    { skip: !auth0AccessToken || !renewal || !renewal?.CanRenewPolicy }
  );

  const createRenewalQuotePayload = {
    StartDate: renewalSummaryData?.RenewalStartDate,
    QuotePartners: [
      {
        PartnerCode: renewalSummaryData?.PartnerCode
      }
    ],
    QuotePolicyHolder: {
      PolicyHolderDateOfBirth: renewalSummaryData?.PolicyHolder?.dateOfBirth,
      Postcode: renewalSummaryData?.PolicyHolder?.postcode,
      State: renewalSummaryData?.State,
      ...(renewalSummaryData?.ClientNumberTypeCode && renewalSummaryData?.ClientNumber
        ? {
            QuoteClientNumbers: [
              {
                ClientNumberTypeCode: renewalSummaryData?.ClientNumberTypeCode,
                Number: renewalSummaryData?.ClientNumber
              }
            ]
          }
        : {})
    },
    QuotePets: [
      {
        PetName: renewalSummaryData?.PetName,
        PetDateOfBirth: renewalSummaryData?.PetDateOfBirth,
        SpeciesCode: renewalSummaryData?.SpeciesCode,
        BreedCode: renewalSummaryData?.BreedCode,
        PolicyNumber: renewalSummaryData?.RenewalNumber,
        Gender: renewalSummaryData?.PetGender,
        Desexed: renewalSummaryData?.Desexed,
        PetOrderNumber: renewalSummaryData?.PetOrderNumber,
        RenewalCover: {
          PlanNumber: renewalSummaryData?.PlanNumber,
          ExcessCode: renewalSummaryData?.ExcessNo,
          LevelOfCoverCode: renewalSummaryData?.LevelOfCoverCode,
          AnnualBenefitLimitCode: renewalSummaryData?.AnnualBenefitLimitCode,
          CurrentExpiringPremium: renewalSummaryData?.PremiumCore,
          ProductCode: PRODUCT_CODE.CORE,
          ...(() => {
            const RenewalOptions = [];

            if (renewalSummaryData?.BoosterCare) {
              RenewalOptions.push({
                ProductCode: PRODUCT_CODE.BOCA,
                CurrentExpiringPremium: renewalSummaryData?.PremiumBOCA
              });
            }

            if (renewalSummaryData?.RoutineCare) {
              RenewalOptions.push({
                ProductCode: PRODUCT_CODE.ROCA,
                CurrentExpiringPremium: renewalSummaryData?.PremiumROCA
              });
            }

            return RenewalOptions.length ? { RenewalOptions } : {};
          })()
        }
      }
    ],
    QuotePaymentFrequencyCodes: [
      {
        PaymentFrequencyCode: renewalSummaryData?.PaymentFrequency
      }
    ],
    QuoteAnnualBenefitLimits: [
      {
        AnnualBenefitLimit: renewalSummaryData?.AnnualBenefitLimit
      }
    ],
    ...(renewalSummaryData?.PromotionCode && renewalSummaryData?.PromotionNo
      ? {
          QuotePromotions: [
            {
              PromotionCode: renewalSummaryData.PromotionCode,
              PromotionNumber: renewalSummaryData.PromotionNo
            }
          ]
        }
      : {})
  };

  const {
    data: renewalQuoteData,
    isLoading: renewalQuoteIsLoading,
    isError: renewalQuoteIsError
  } = useCreateRenewalQuoteQuery(
    { data: createRenewalQuotePayload, auth0Token: auth0AccessToken },
    { skip: !auth0AccessToken || !renewalSummaryData }
  );

  useEffect(() => {
    if (renewalSummaryData) {
      dispatch(setRenewalSummary(renewalSummaryData));
    }
  }, [dispatch, renewalSummaryData]);

  const selectedPremium = renewalQuoteData?.Pets?.[0]?.Premiums?.find(
    (p) =>
      p.ProductCode === PRODUCT_CODE.CORE &&
      p.ExcessCode === adjustedSummary?.ExcessCode &&
      p.LevelOfCoverCode === adjustedSummary?.LevelOfCoverCode
  );

  const newPremium = adjustedSummary?.newPremium ?? 0;
  const freeMonthDiscount = adjustedSummary?.freeMonthDiscount ?? 0;
  const newPremiumAnnual = adjustedSummary?.newPremiumAnnual ?? 0;

  const selectedOptionCovers = (selectedPremium?.OptionalCovers || []).filter((cover) => {
    if (cover.ProductCode === PRODUCT_CODE.ROCA) {
      return !!adjustedSummary?.RoutineCare;
    }

    if (cover.ProductCode === PRODUCT_CODE.BOCA) {
      return !!adjustedSummary?.BoosterCare;
    }

    return false;
  });

  const premiumPayload = (() => {
    if (!selectedPremium) {
      return null;
    }

    const { OptionalCovers, ...premiumWithoutOptionalCovers } = selectedPremium;

    return {
      ...premiumWithoutOptionalCovers,
      optionalCovers: selectedOptionCovers.map((cover) => {
        const isNewBusinessOptionalCover =
          (cover.ProductCode === PRODUCT_CODE.ROCA && renewalSummaryData?.RoutineCare === false) ||
          (cover.ProductCode === PRODUCT_CODE.BOCA && renewalSummaryData?.BoosterCare === false);

        return {
          ...cover,
          ...(isNewBusinessOptionalCover &&
          renewalQuoteData?.IsNewBusinessApiError === false &&
          renewalQuoteData?.NewBusinessResponse
            ? {
                OnlineQuoteNumber: renewalQuoteData?.NewBusinessResponse?.OnlineQuoteNumber,
                QuoteReferenceNumber: renewalQuoteData?.NewBusinessResponse?.QuoteReferenceNumber,
                QuotePetId: renewalQuoteData?.NewBusinessResponse?.QuotePetId
              }
            : {})
        };
      })
    };
  })();

  const submitRenewalPayload = {
    PartnerNo: renewalSummaryData?.PartnerNo,
    OnlineQuoteNumber: renewalQuoteData?.OnlineQuoteNumber,
    QuoteReferenceNumber: renewalQuoteData?.QuoteReferenceNumber,
    PartnerCode: renewalSummaryData?.PartnerCode,
    PolicyId: renewalSummaryData?.PolicyNumber,
    StartDate: renewalSummaryData?.RenewalStartDate,
    EndDate: renewalSummaryData?.RenewalEndDate,
    PaymentFrequencyCode: renewalSummaryData?.PaymentFrequency,
    PaymentMethodCode: renewalSummaryData?.PaymentMethodCode,
    FirstCollectionDate: renewalSummaryData?.FirstCollectionDate,
    ExternalId: '',
    PolicyHolder: {
      TitleCode: renewalSummaryData?.PolicyHolder?.title,
      FirstName: renewalSummaryData?.PolicyHolder?.firstName,
      LastName: renewalSummaryData?.PolicyHolder?.lastName,
      PolicyHolderDateOfBirth: renewalSummaryData?.PolicyHolder?.dateOfBirth,
      Street: renewalSummaryData?.PolicyHolder?.street,
      Suburb: renewalSummaryData?.PolicyHolder?.suburb,
      Postcode: renewalSummaryData?.PolicyHolder?.postcode,
      State: renewalSummaryData?.State,
      Email: renewalSummaryData?.PolicyHolder?.email,
      PhoneHome: renewalSummaryData?.PolicyHolder?.phoneHome,
      PhoneMobile: renewalSummaryData?.PolicyHolder?.phoneMobile,
      Gender: renewalSummaryData?.PolicyHolder?.gender,
      ...(renewalSummaryData?.ClientNumberTypeCode && renewalSummaryData?.ClientNumber
        ? {
            ClientNumbers: [
              {
                ClientNumberTypeCode: renewalSummaryData?.ClientNumberTypeCode,
                Number: renewalSummaryData?.ClientNumber
              }
            ]
          }
        : {})
    },
    ...(renewalSummaryData?.PromotionCode && renewalSummaryData?.PromotionNo
      ? {
          Promotions: [
            {
              PromotionCode: renewalSummaryData.PromotionCode,
              PromotionNo: renewalSummaryData.PromotionNo
            }
          ]
        }
      : {}),
    Pets: [
      {
        QuotePetId: renewalQuoteData?.Pets?.[0]?.PetID,
        PetName: renewalQuoteData?.Pets?.[0]?.PetName,
        SpeciesCode: renewalSummaryData?.SpeciesCode,
        PetDateOfBirth: renewalSummaryData?.PetDateOfBirth,
        BreedCode: renewalSummaryData?.BreedCode,
        CrossBreed: renewalSummaryData?.CrossBreed,
        Gender: renewalSummaryData?.PetGender,
        Desexed: renewalSummaryData?.Desexed,
        PetNumber: renewalSummaryData?.PetOrderNumber,
        Microchip: renewalSummaryData?.Microchip,
        MicrochipNumber: renewalSummaryData?.MicrochipNumber,
        AssistanceDog: renewalSummaryData?.AssistanceDog,
        PolicyNumber: renewalSummaryData?.RenewalNumber,
        Premium: premiumPayload
      }
    ]
  };

  const handleSubmitRenewalAdjustment = async () => {
    let result;

    try {
      result = await submitRenewal({ data: submitRenewalPayload, auth0Token: auth0AccessToken }).unwrap();
    } catch (error) {
      // .unwrap() rejects on any non-2xx, so without this a 500 or a timeout emitted no result at
      // all - indistinguishable in GA4 from a customer closing the tab. Re-thrown because the
      // mutation's own isError drives the error page, and swallowing would hide a real failure.
      getGtmService().pushAdjustCoverSubmitResult({ policyNumber, result: 'error' });
      throw error;
    }

    // A resolved response with no message used to throw here, so that path reached no push and no
    // error page either. It now falls through to the error branch.
    const isNotEligible =
      typeof result?.message === 'string' &&
      result.message.includes('Unable to submit, policy is not eligible for renewal');

    if (result && result.responseObject > 0) {
      getGtmService().pushAdjustCoverSubmitResult({ policyNumber, result: 'success' });
      dispatch(setRenewalSummary(renewalSummaryData));
      navigate(CSPUrlsMap.renewalHubConfirmation.url);
    } else if (isNotEligible) {
      getGtmService().pushAdjustCoverSubmitResult({ policyNumber, result: 'not_eligible' });
      setUnableToSubmit(true);
    } else {
      getGtmService().pushAdjustCoverSubmitResult({ policyNumber, result: 'error' });
      setSubmitRenewalHasError(true);
    }
  };

  return {
    renewal,
    isMobile,
    adjustedSummary,
    renewalSummaryData,
    renewalSummaryIsLoading,
    renewalSummaryIsError,
    renewalQuoteData,
    renewalQuoteIsLoading,
    handleSubmitRenewalAdjustment,
    submitRenewalIsLoading,
    submitRenewalIsError: submitRenewalIsError || submitRenewalHasError,
    renewalEligibilityIsLoading,
    newPremiumAnnual,
    auth0AccessToken,
    policyNumber,
    renewalEligibilityIsError,
    renewalQuoteIsError,
    unableToSubmit
  };
};
